import { Vector3, Matrix } from '@babylonjs/core/Maths/math';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { meleeSwing } from './melee-arc.mjs';

// Training dummies: capsule body + head on a post. They flash and rock when
// hit, fall over at 0 HP, and stand back up a few seconds later. Damage
// numbers float up as DOM elements so combat feel is testable without AI.

const SPOTS = [[2.5, 0, 4], [-3.5, 0, 7], [6, 0, -1], [0, 0, 9]];

export class Dummies {
  constructor(scene, engine, camera, shadows) {
    this.scene = scene;
    this.engine = engine;
    this.camera = camera;
    // main.js hands the shared world object over right after it builds it
    // (dummies are constructed before `world` exists). meleeHit needs it for
    // world.query (occlusion) and world.player.attack (the swing id); with it
    // unset the arc falls back to the pre-query behaviour, which is what the
    // gym wants anyway — there are no walls there.
    this.world = null;
    this.list = [];
    for (const [x, y, z] of SPOTS) this._make(x, z, shadows);
  }

  _make(x, z, shadows) {
    const scene = this.scene;
    const root = new TransformNode('dummyRoot', scene);
    root.position.set(x, 0, z);
    const tilt = new TransformNode('dummyTilt', scene);
    tilt.parent = root;

    const mat = new StandardMaterial('dummyMat', scene);
    mat.diffuseColor = new Color3(0.55, 0.5, 0.42);
    mat.specularColor = Color3.Black();

    const body = MeshBuilder.CreateCapsule('dummyBody', { height: 1.15, radius: 0.24 }, scene);
    body.position.y = 1.0; body.material = mat; body.parent = tilt;
    const head = MeshBuilder.CreateSphere('dummyHead', { diameter: 0.32 }, scene);
    head.position.y = 1.78; head.material = mat; head.parent = tilt;
    const post = MeshBuilder.CreateCylinder('dummyPost', { height: 0.5, diameter: 0.1 }, scene);
    post.position.y = 0.25; post.material = mat; post.parent = tilt;

    const d = {
      root, tilt, mat, body, head, post,
      hp: 100, alive: true, respawnT: 0,
      rock: 0, rockDir: new Vector3(), flash: 0, fall: 0,
    };
    body.metadata = head.metadata = post.metadata = { dummy: d, headshot: false };
    head.metadata = { dummy: d, headshot: true };
    for (const m of [body, head, post]) { shadows?.addShadowCaster(m); m.receiveShadows = true; }
    this.list.push(d);
  }

  // Melee arc check from the player. Returns number of dummies hit.
  //
  // This used to be its own copy of the arc test (reach + 0.3, dot 0.45, no y
  // band, no occlusion, no per-swing memory) — one of four that disagreed.
  // The rules now live in ONE place: src/world/melee-arc.mjs. The damage call
  // below is untouched, so a dummy still takes exactly what it took before.
  meleeHit(playerPos, heading, reach, dmg) {
    let hits = 0;
    const swing = meleeSwing(this.world, playerPos, heading, reach);
    const to = new Vector3();
    for (const d of this.list) {
      if (!d.alive) continue;
      const p = d.root.position;
      if (!swing.test(p.x, p.y, p.z, d)) continue;
      // rock direction: flat, away from the player, as before
      to.set(p.x - playerPos.x, 0, p.z - playerPos.z).normalize();
      this.damage(d, dmg, to, false);
      hits++;
    }
    return hits;
  }

  // `zone` is the world query's 'head' | 'body' | 'legs'. Nothing here uses
  // it — a training dummy has no zone multipliers — it is forwarded so the
  // infected, who ride this path on purpose (see below), do not lose it.
  damage(d, dmg, fromDir, headshot, zone) {
    // The infected ride this path on purpose: controller.js's hitscan ray only
    // accepts meshes named ground/crate/dummy*, and its damage switch routes
    // metadata.dummy straight here. world/zombies.js parks `dummy…`-named
    // hitboxes on its entities carrying an { infected, owner } proxy, so gun
    // hits resolve without a single line changing in src/player/*.
    if (d?.infected != null) { d.owner.gunHit(d, dmg, fromDir, headshot, zone); return; }
    if (!d.alive) return;
    const total = headshot ? dmg * 2 : dmg;
    d.hp -= total;
    d.flash = 1;
    d.rock = Math.min(1, d.rock + 0.5 + total * 0.008);
    d.rockDir.copyFrom(fromDir).normalize();
    this._floater(d, total, headshot);
    if (d.hp <= 0) { d.alive = false; d.respawnT = 4; }
  }

  _floater(d, dmg, headshot) {
    const p = d.head.getAbsolutePosition();
    const w = this.engine.getRenderWidth(), h = this.engine.getRenderHeight();
    const sp = Vector3.Project(p, Matrix.Identity(), this.scene.getTransformMatrix(),
      this.camera.cam.viewport.toGlobal(w, h));
    if (sp.z > 1) return;
    const el = document.createElement('div');
    el.textContent = headshot ? `${dmg} HS!` : `${dmg}`;
    el.style.cssText = `position:fixed;left:${sp.x}px;top:${sp.y}px;color:${headshot ? '#ff5a3c' : '#e8d9a0'};` +
      'font:bold 16px monospace;pointer-events:none;z-index:20;transition:all .7s ease-out;text-shadow:0 1px 2px #000;';
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.top = (sp.y - 60) + 'px'; el.style.opacity = 0; });
    setTimeout(() => el.remove(), 750);
  }

  update(dt, focus = null) {
    // The four training dummies stand at the gym origin. In map mode the player
    // is 2-4 km away and they are 28k triangles of capsule/sphere tessellation
    // that nobody can see, counted in every frame's budget. Hide them once they
    // are past any possible read; the gym never leaves the ring.
    if (focus) {
      const on = (focus.x * focus.x + focus.z * focus.z) < 300 * 300;
      if (on !== this.shown) {
        this.shown = on;
        for (const d of this.list) for (const m of [d.body, d.head, d.post]) m?.setEnabled(on);
      }
      if (!on) return;
    }
    for (const d of this.list) {
      // hit flash
      if (d.flash > 0) {
        d.flash = Math.max(0, d.flash - dt * 5);
        d.mat.emissiveColor = new Color3(d.flash * 0.6, d.flash * 0.08, d.flash * 0.05);
      }
      // rock away from the hit and spring back
      if (d.rock > 0 && d.alive) {
        d.rock = Math.max(0, d.rock - dt * 3);
        const a = Math.sin(d.rock * Math.PI) * 0.35;
        d.tilt.rotation.x = a * d.rockDir.z;
        d.tilt.rotation.z = -a * d.rockDir.x;
      }
      // death fall / respawn
      if (!d.alive) {
        d.fall = Math.min(1, d.fall + dt * 3);
        d.tilt.rotation.x = d.fall * (Math.PI / 2 - 0.06) * (d.rockDir.z >= 0 ? 1 : -1);
        d.respawnT -= dt;
        if (d.respawnT <= 0) {
          d.alive = true; d.hp = 100; d.fall = 0; d.rock = 0;
          d.tilt.rotation.set(0, 0, 0);
        }
      }
    }
  }
}
